/**
 * rsvpService.js
 *
 * Servicio que encapsula el envío del formulario RSVP a Google Apps Script.
 * El script de Google inserta UNA FILA POR PERSONA en el Google Sheet:
 *   - Fila del invitado principal  (Tipo = "Principal")
 *   - Una fila por cada acompañante (Tipo = "Acompañante", con columna Acompanante_de)
 *
 * El payload enviado tiene 3 objetos:
 *   - invitado    → datos personales del titular
 *   - evento      → asistencia y transporte
 *   - acompanantes → array con los datos de cada acompañante
 *
 * Variable de entorno necesaria:
 *   REACT_APP_GOOGLE_SCRIPT_URL  →  URL de la Web App publicada en Google Apps Script
 */

const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL;

/**
 * Envía los datos del formulario RSVP al Google Apps Script.
 *
 * @param {Object} formData - Estado del formulario React:
 *   {
 *     nombre:        string,
 *     asistencia:    'si' | 'no',
 *     transporte:    'privado' | 'bus' | '',
 *     acompanantes:  string,
 *     restricciones: string,
 *     guestDetails:  Array<{ nombre: string, restricciones: string, isChild: boolean }>
 *   }
 *
 * @returns {Promise<{ success: boolean, message: string }>}
 */
export async function submitRSVP(formData) {
  if (!GOOGLE_SCRIPT_URL) {
    console.error(
      '[rsvpService] REACT_APP_GOOGLE_SCRIPT_URL no está definida. ' +
      'Añádela en el archivo .env para conectar con Google Sheets.'
    );
    if (process.env.NODE_ENV === 'development') {
      const payload = buildPayload(formData);
      console.log('[rsvpService] Modo desarrollo — payload que se enviaría:', payload);
      return { success: true, message: 'Simulado en desarrollo (sin URL configurada)' };
    }
    return { success: false, message: 'URL de Google Script no configurada.' };
  }

  try {
    // Google Apps Script acepta POST y devuelve JSON
    const response = await fetch(GOOGLE_SCRIPT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(buildPayload(formData)),
      redirect: 'follow'
    });

    // Intentar leer la respuesta JSON
    const text = await response.text();
    let result;
    try {
      result = JSON.parse(text);
    } catch (e) {
      // Si no es JSON válido, asumimos éxito (modo legacy)
      console.log('[rsvpService] Respuesta no-JSON (probablemente exitosa)');
      return { success: true, message: 'RSVP enviado correctamente.' };
    }

    // Verificar si hay error en la respuesta
    if (result.status === 'error') {
      return { success: false, message: result.message || 'Error al procesar el formulario.' };
    }

    return { success: true, message: result.message || 'RSVP enviado correctamente.' };
  } catch (error) {
    console.error('[rsvpService] Error al enviar el RSVP:', error);
    return { success: false, message: 'Error al enviar el formulario. Inténtalo de nuevo.' };
  }
}

/**
 * Construye el payload con 3 objetos diferenciados.
 *
 * Estructura resultante:
 * {
 *   password:  string,
 *   invitado: {
 *     nombre:        string,
 *     restricciones: string
 *   },
 *   evento: {
 *     asistencia: 'si' | 'no',
 *     transporte: 'privado' | 'bus' | ''
 *   },
 *   acompanantes: Array<{
 *     nombre:        string,
 *     restricciones: string,
 *     isChild:       boolean
 *   }>
 * }
 *
 * El script de Google convierte esto en:
 *   - 1 fila para el invitado principal
 *   - N filas para los acompañantes (con columna Acompanante_de = nombre del titular)
 *
 * @param {Object} formData
 * @returns {Object}
 */
function buildPayload(formData) {
  return {
    password: formData.password || '',
    invitado: {
      nombre:        formData.nombre        || '',
      restricciones: formData.restricciones || ''
    },
    evento: {
      asistencia: formData.asistencia || '',
      transporte: formData.transporte || ''
    },
    acompanantes: (formData.guestDetails || []).map((guest) => ({
      nombre:        guest.nombre        || '',
      restricciones: guest.restricciones || '',
      isChild:       Boolean(guest.isChild)
    }))
  };
}
